import React, { useState, useEffect } from 'react';
import { postAPI } from '../../../app/api';
import Swal from 'sweetalert2';
import editIcon from "../../../theme/assets/images/icon/edit-icon.svg";
import deleteIcon from "../../../theme/assets/images/icon/delete-icon.svg";
import downArrowIcon from "../../../theme/assets/images/icon/down-arrow.svg";
import moreIcon from "../../../theme/assets/images/icon/more-icon.svg";

const EmailList = () => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openGroup, setOpenGroup] = useState(null);
    const [groupMembers, setGroupMembers] = useState({});
    const [editingGroup, setEditingGroup] = useState(null);
    const [editGroupName, setEditGroupName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [showAddMemberModal, setShowAddMemberModal] = useState(null);
    const [allMembers, setAllMembers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedNewMembers, setSelectedNewMembers] = useState([]);

    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const response = await postAPI('communication-group/list', {});
            if (response?.status === 1) {
                setGroups(response?.data || []);
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
            Swal.fire('Error', 'Failed to fetch groups', 'error');
        } finally {
            setLoading(false);
        }
    };

    const toggleMember = async (groupId) => {
        if (openGroup === groupId) {
            setOpenGroup(null);
        } else {
            setOpenGroup(groupId);
            // Fetch group members if not already loaded
            if (!groupMembers[groupId]) {
                try {
                    const response = await postAPI('communication-group/fetchSingleData', { id: groupId });
                    if (response?.status === 1) {
                        setGroupMembers(prev => ({
                            ...prev,
                            [groupId]: response.data.members || []
                        }));
                    }
                } catch (error) {
                    console.error('Error fetching group members:', error);
                }
            }
        }
    };

    const handleDeleteGroup = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this group?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('communication-group/delete', { id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Group deleted successfully', 'success');
                    fetchGroups();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete group', 'error');
            }
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const response = await postAPI('communication-group/toggleStatus', { id });
            if (response?.status === 1) {
                Swal.fire('Success', 'Status updated successfully', 'success');
                fetchGroups();
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };

    const handleEditGroup = (group) => {
        setEditingGroup(group.id);
        setEditGroupName(group.group_name);
        setEditDescription(group.description || '');
    };

    const handleSaveEdit = async (groupId) => {
        if (!editGroupName.trim()) {
            Swal.fire('Validation', 'Group name is required', 'warning');
            return;
        }

        try {
            const members = groupMembers[groupId] || [];
            const response = await postAPI('communication-group/create', {
                id: groupId,
                group_name: editGroupName,
                description: editDescription,
                members: members.map(m => ({
                    user_code: m.user_code,
                    first_name: m.first_name,
                    last_name: m.last_name,
                    email_id: m.email_id,
                    mobile_number: m.mobile_number
                }))
            });

            if (response?.status === 1) {
                Swal.fire('Success', 'Group updated successfully', 'success');
                setEditingGroup(null);
                fetchGroups();
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to update group', 'error');
        }
    };

    const handleCancelEdit = () => {
        setEditingGroup(null);
        setEditGroupName('');
        setEditDescription('');
    };

    const handleOpenAddMember = async (groupId) => {
        setShowAddMemberModal(groupId);
        setSelectedNewMembers([]);
        setSearchTerm('');
        
        try {
            const response = await postAPI('communication-group/getAllMembers', {});
            console.log('getAllMembers response:', response);
            
            if (response?.status === 1) {
                const allMembersData = response.data || [];
                console.log('All members data:', allMembersData);
                
                // Filter out members already in the group
                const existingMemberCodes = (groupMembers[groupId] || []).map(m => m.user_code);
                console.log('Existing member codes:', existingMemberCodes);
                
                const availableMembers = allMembersData.filter(
                    m => !existingMemberCodes.includes(m.user_code)
                );
                console.log('Available members:', availableMembers);
                
                setAllMembers(availableMembers);
                
                if (availableMembers.length === 0) {
                    Swal.fire('Info', 'All members are already added to this group', 'info');
                }
            } else {
                Swal.fire('Error', response?.msg || 'Failed to fetch members', 'error');
            }
        } catch (error) {
            console.error('Error fetching members:', error);
            Swal.fire('Error', 'Failed to fetch members: ' + error.message, 'error');
        }
    };

    const handleAddMembersToGroup = async () => {
        if (selectedNewMembers.length === 0) {
            Swal.fire('Validation', 'Please select at least one member', 'warning');
            return;
        }

        try {
            let successCount = 0;
            let failCount = 0;

            for (const member of selectedNewMembers) {
                try {
                    console.log('Adding member:', member);
                    
                    const response = await postAPI('communication-group/addMember', {
                        group_id: showAddMemberModal,
                        user_code: member.user_code,
                        first_name: member.first_name || '',
                        last_name: member.last_name || '',
                        email_id: member.email_id || '',
                        mobile_number: member.mobile_number || ''
                    });

                    console.log('Add member response:', response);

                    if (response?.status === 1) {
                        successCount++;
                    } else {
                        failCount++;
                        console.error('Failed to add member:', response);
                    }
                } catch (error) {
                    failCount++;
                    console.error('Error adding member:', error);
                }
            }

            if (successCount > 0) {
                Swal.fire('Success', `${successCount} member(s) added successfully${failCount > 0 ? `, ${failCount} failed` : ''}`, 'success');
                setShowAddMemberModal(null);
                setSelectedNewMembers([]);
                
                // Refresh group members
                setGroupMembers(prev => {
                    const updated = { ...prev };
                    delete updated[showAddMemberModal];
                    return updated;
                });
                
                if (openGroup === showAddMemberModal) {
                    setOpenGroup(null);
                    setTimeout(() => toggleMember(showAddMemberModal), 100);
                }
                fetchGroups();
            } else {
                Swal.fire('Error', 'Failed to add members. Please check console for details.', 'error');
            }
        } catch (error) {
            console.error('Add members error:', error);
            Swal.fire('Error', 'Failed to add members: ' + error.message, 'error');
        }
    };

    const handleRemoveMember = async (groupId, memberId) => {
        const result = await Swal.fire({
            title: 'Remove Member',
            text: 'Are you sure you want to remove this member from the group?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('communication-group/removeMember', { id: memberId });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Member removed successfully', 'success');
                    
                    // Refresh group members
                    setGroupMembers(prev => ({
                        ...prev,
                        [groupId]: prev[groupId].filter(m => m.id !== memberId)
                    }));
                    fetchGroups();
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to remove member', 'error');
            }
        }
    };

    const toggleMemberSelection = (member) => {
        const isSelected = selectedNewMembers.some(m => m.user_code === member.user_code);
        if (isSelected) {
            setSelectedNewMembers(selectedNewMembers.filter(m => m.user_code !== member.user_code));
        } else {
            setSelectedNewMembers([...selectedNewMembers, member]);
        }
    };

    const filteredMembers = allMembers.filter(member => {
        const fullName = `${member.first_name || ''} ${member.middle_name || ''} ${member.last_name || ''}`.trim().toLowerCase();
        const email = (member.email_id || '').toLowerCase();
        const phone = member.mobile_number || '';
        const searchLower = searchTerm.toLowerCase();
        
        return fullName.includes(searchLower) ||
            email.includes(searchLower) ||
            phone.includes(searchTerm);
    });

    return (
        <div className='email-list'>
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : groups.length > 0 ? (
                <ul className='m-0 p-0'>
                    {groups.map((group, index) => (
                        <li className='card' key={group.id}>
                            <div className='card-body'>
                                <div className='d-flex align-items-center justify-content-between mb-0'>
                                    {editingGroup === group.id ? (
                                        <div className='flex-grow-1 me-3'>
                                            <input
                                                type="text"
                                                className='form-control mb-2'
                                                placeholder='Group Name'
                                                value={editGroupName}
                                                onChange={(e) => setEditGroupName(e.target.value)}
                                            />
                                            <textarea
                                                className='form-control'
                                                placeholder='Description (optional)'
                                                rows={2}
                                                value={editDescription}
                                                onChange={(e) => setEditDescription(e.target.value)}
                                            />
                                        </div>
                                    ) : (
                                        <h3>
                                            {group.group_name} 
                                            <span className={`badge ms-2 badge-${group.status === 1 ? 'success' : 'danger'}`}>
                                                {group.status === 1 ? 'Active' : 'Inactive'}
                                            </span>
                                            <small className='ms-2 text-muted'>({group.member_count} members)</small>
                                        </h3>
                                    )}
                                    <div className='action d-flex align-items-center gap-0'>
                                        {editingGroup === group.id ? (
                                            <>
                                                <button 
                                                    className='btn btn-sm btn-success me-1'
                                                    onClick={() => handleSaveEdit(group.id)}
                                                >
                                                    Save
                                                </button>
                                                <button 
                                                    className='btn btn-sm btn-secondary'
                                                    onClick={handleCancelEdit}
                                                >
                                                    Cancel
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button 
                                                    className='btn btn-sm' 
                                                    onClick={() => toggleMember(group.id)}
                                                    title="View Members"
                                                >
                                                    <img src={downArrowIcon} width={'24px'} alt="Toggle" />
                                                </button>
                                                <button 
                                                    className='btn btn-sm'
                                                    onClick={() => handleEditGroup(group)}
                                                    title="Edit Group"
                                                >
                                                    <img src={editIcon} width={'24px'} alt="Edit" />
                                                </button>
                                                <button 
                                                    className='btn btn-sm'
                                                    onClick={() => handleDeleteGroup(group.id)}
                                                    title="Delete Group"
                                                > 
                                                    <img src={deleteIcon} width={'24px'} alt="Delete" />
                                                </button> 
                                                <div className='dropdown'>
                                                    <button 
                                                        className='btn btn-sm' 
                                                        data-bs-toggle="dropdown" 
                                                        aria-expanded="false"
                                                    >
                                                        <img src={moreIcon} width={'24px'} alt="More" />
                                                    </button>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li>
                                                            <button 
                                                                className="dropdown-item"
                                                                onClick={() => handleToggleStatus(group.id, group.status)}
                                                            >
                                                                {group.status === 1 ? 'Make Inactive' : 'Make Active'}
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="dropdown-item"
                                                                onClick={() => handleOpenAddMember(group.id)}
                                                            >
                                                                Add Members
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                
                                {!editingGroup && group.description && (
                                    <p className='text-muted mb-2'>{group.description}</p>
                                )}
                                
                                {openGroup === group.id && (
                                    <>
                                        <hr></hr>
                                        <div className='member-list'>
                                            <div className='d-flex justify-content-between align-items-center mb-3'>
                                                <h4 className='mb-0'>Members ({groupMembers[group.id]?.length || 0})</h4>
                                                <button 
                                                    className='btn btn-sm btn-primary'
                                                    onClick={() => handleOpenAddMember(group.id)}
                                                >
                                                    <i className="bi bi-plus-circle me-1"></i>
                                                    Add Members
                                                </button>
                                            </div>
                                            {groupMembers[group.id] && groupMembers[group.id].length > 0 ? (
                                                <table className='table table-striped table-hover cus-table'>
                                                    <thead className='table-dark'>
                                                        <tr>
                                                            <th>Name</th>
                                                            <th>Email</th>
                                                            <th>Phone</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {groupMembers[group.id].map((member, idx) => (
                                                            <tr key={idx}>
                                                                <td>{member.first_name} {member.last_name}</td>
                                                                <td>{member.email_id || '-'}</td>
                                                                <td>{member.mobile_number || '-'}</td>
                                                                <td>
                                                                    <button 
                                                                        className='btn btn-sm btn-danger'
                                                                        onClick={() => handleRemoveMember(group.id, member.id)}
                                                                        title="Remove Member"
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <p className='text-center text-muted'>No members in this group</p>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            ) : (
                <div className='card'>
                    <div className='card-body text-center py-5'>
                        <p className='text-muted mb-0'>No groups found. Create a new group to get started.</p>
                    </div>
                </div>
            )}
            
            {/* Add Member Modal */}
            {showAddMemberModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Members to Group</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setShowAddMemberModal(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by name, email or phone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                
                                {selectedNewMembers.length > 0 && (
                                    <div className="alert alert-info">
                                        <strong>{selectedNewMembers.length}</strong> member(s) selected
                                    </div>
                                )}

                                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <table className="table table-hover cus-table">
                                        <thead className="sticky-top bg-white">
                                            <tr>
                                                <th width="50">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={selectedNewMembers.length === filteredMembers.length && filteredMembers.length > 0}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedNewMembers(filteredMembers);
                                                            } else {
                                                                setSelectedNewMembers([]);
                                                            }
                                                        }}
                                                    />
                                                </th>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredMembers.length > 0 ? (
                                                filteredMembers.map((member) => (
                                                    <tr key={member.user_code}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selectedNewMembers.some(m => m.user_code === member.user_code)}
                                                                onChange={() => toggleMemberSelection(member)}
                                                            />
                                                        </td>
                                                        <td>
                                                            {`${member.first_name || ''} ${member.middle_name || ''} ${member.last_name || ''}`.trim() || '-'}
                                                        </td>
                                                        <td>{member.email_id || '-'}</td>
                                                        <td>{member.mobile_number || '-'}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center text-muted">
                                                        {searchTerm ? 'No members found matching your search' : 'No members available to add'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowAddMemberModal(null)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={handleAddMembersToGroup}
                                    disabled={selectedNewMembers.length === 0}
                                >
                                    Add {selectedNewMembers.length > 0 && `(${selectedNewMembers.length})`} Members
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default EmailList;